export interface PersistedComposerEntry {
	content: string
	commentContent: string
	source: "user" | "ai" | null
	appliedAiDraftAt: number | null
	updatedAt: number
}

// One entry per thread the user has ever opened, each holding editor HTML that
// can carry inlined images, so the store grows without bound until the browser
// refuses the write.
export const MAX_PERSISTED_ENTRIES = 40
export const MAX_ENTRY_BYTES = 100_000

export function isEmptyEntry(entry: PersistedComposerEntry): boolean {
	return (
		isBlankHtml(entry.content) &&
		isBlankHtml(entry.commentContent) &&
		entry.appliedAiDraftAt === null
	)
}

function isBlankHtml(value: string): boolean {
	return value.replace(/<[^>]*>/g, "").trim().length === 0
}

function entryBytes(entry: PersistedComposerEntry): number {
	return entry.content.length + entry.commentContent.length
}

export function pruneEntries(
	entries: Record<string, PersistedComposerEntry>,
	limit = MAX_PERSISTED_ENTRIES,
): Record<string, PersistedComposerEntry> {
	const keepable = Object.entries(entries).filter(
		([, entry]) => !isEmptyEntry(entry) && entryBytes(entry) <= MAX_ENTRY_BYTES,
	)

	const newestFirst = keepable.sort(
		([, a], [, b]) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0),
	)

	return Object.fromEntries(newestFirst.slice(0, limit))
}

export function isQuotaError(error: unknown): boolean {
	if (!(error instanceof Error)) return false
	return (
		error.name === "QuotaExceededError" ||
		error.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
		error.message.toLowerCase().includes("quota")
	)
}

export function writeWithQuotaRecovery({
	key,
	value,
	setItem,
	removeItem,
	onDropped,
}: {
	key: string
	value: string
	setItem: (key: string, value: string) => void
	removeItem: (key: string) => void
	onDropped?: (keptEntries: number) => void
}): void {
	try {
		setItem(key, value)
		return
	} catch (error) {
		if (!isQuotaError(error)) throw error
	}

	const parsed = safeParse(value)
	const entries = parsed?.state?.entries

	if (entries) {
		for (const limit of [10, 3, 1]) {
			const kept = pruneEntries(entries, limit)
			try {
				setItem(
					key,
					JSON.stringify({
						...parsed,
						state: { ...parsed.state, entries: kept },
					}),
				)
				onDropped?.(Object.keys(kept).length)
				return
			} catch (error) {
				if (!isQuotaError(error)) throw error
			}
		}
	}

	try {
		removeItem(key)
		onDropped?.(0)
	} catch {
		return
	}
}

interface PersistedShape {
	state?: { entries?: Record<string, PersistedComposerEntry> }
}

function safeParse(value: string): PersistedShape | null {
	try {
		return JSON.parse(value) as PersistedShape
	} catch {
		return null
	}
}
